import type {
  AccreditationsResponse,
  CFRRegulationSection,
  ComprehensiveRequirementsResponse,
  OperationalDocument,
  RegulatoryRequirement,
  StateRequirementsResponse,
} from "@services/facilityCompliance";

export type ComplianceSearchTab = "federal" | "state" | "operational" | "other";

export type ComplianceSearchSourceType =
  | "regulatory"
  | "cfr"
  | "operational"
  | "state_facility"
  | "accreditation"
  | "cfr_text"
  | "federal_register";

export interface ComplianceSearchItem {
  id: string;
  sourceType: ComplianceSearchSourceType;
  tab: ComplianceSearchTab;
  groupLabel: string;
  title: string;
  searchableText: string;
  snippetSource: string;
  expandKey: string;
  sectionIdentifier?: string;
  sectionUrl?: string;
  /** Pre-rendered snippet from API (Phase 1.5); when set, UI prefers over extractSearchSnippet */
  apiSnippet?: string;
}

export interface RegulationTextSearchHit {
  source_type: "cfr_text" | "federal_register";
  group_label: string;
  title: string;
  snippet: string;
  section_identifier?: string | null;
  cfr_title?: number | null;
  section_url?: string | null;
  regulation_group?: string | null;
  regulation_section_id?: number | null;
  rank?: number;
}

export interface BuildComplianceSearchIndexInput {
  comprehensiveRequirements: ComprehensiveRequirementsResponse | null;
  stateRequirements: StateRequirementsResponse | null;
  accreditationData: AccreditationsResponse | null;
  isStateLevelRequirement: (req: {
    jurisdiction_level?: string;
    jurisdiction_name?: string | null;
    regulatory_body?: string | null;
  }) => boolean;
}

function joinFields(...parts: (string | null | undefined)[]): string {
  return parts.filter(Boolean).join(" ");
}

function categoryGroupLabel(category: string | null | undefined): string {
  const c = (category ?? "").trim();
  return c ? c.toUpperCase() : "REGULATORY REQUIREMENTS";
}

function cfrGroupLabel(groupShortName: string | null | undefined): string {
  const g = (groupShortName ?? "").trim();
  return g ? `CFR — ${g.toUpperCase()}` : "CFR";
}

function cfrTitle(cfr: CFRRegulationSection): string {
  const id = cfr.ecfr_identifier || cfr.section_identifier || "";
  const name = cfr.section_name ?? "";
  if (id && name) return `${id} - ${name}`;
  return id || name || "CFR Section";
}

function buildRegulatoryItems(
  reqs: RegulatoryRequirement[],
  tab: ComplianceSearchTab,
  isStateLevelRequirement: BuildComplianceSearchIndexInput["isStateLevelRequirement"],
): ComplianceSearchItem[] {
  return reqs.map((req) => {
    const searchableText = joinFields(
      req.title,
      req.description,
      req.summary,
      req.category,
      req.regulatory_body,
      req.regulation_reference,
      req.requirement_code,
    );
    return {
      id: `regulatory-${req.id}`,
      sourceType: "regulatory",
      tab,
      groupLabel: categoryGroupLabel(req.category),
      title: req.title,
      searchableText,
      snippetSource: req.description || req.summary || req.title,
      expandKey: String(req.id),
    };
  });
}

function buildCfrItems(sections: CFRRegulationSection[]): ComplianceSearchItem[] {
  return sections.map((cfr) => {
    const searchableText = joinFields(
      cfr.section_name,
      cfr.part_name,
      cfr.part_number,
      cfr.group_short_name,
      cfr.group_name,
      cfr.section_identifier,
      cfr.ecfr_identifier,
    );
    return {
      id: `cfr-${cfr.regulation_section_id}`,
      sourceType: "cfr",
      tab: "federal",
      groupLabel: cfrGroupLabel(cfr.group_short_name),
      title: cfrTitle(cfr),
      searchableText,
      snippetSource: cfr.section_name || cfr.part_name || cfrTitle(cfr),
      expandKey: `cfr-${cfr.regulation_section_id}`,
      sectionIdentifier: cfr.ecfr_identifier || cfr.section_identifier || undefined,
      sectionUrl: cfr.section_url ?? undefined,
    };
  });
}

function buildOperationalItems(docs: OperationalDocument[]): ComplianceSearchItem[] {
  return docs.map((doc) => {
    const searchableText = joinFields(
      doc.document_name,
      doc.document_code,
      doc.notes,
      doc.compliance_type,
    );
    return {
      id: `operational-${doc.id}`,
      sourceType: "operational",
      tab: "operational",
      groupLabel: "OPERATIONAL DOCUMENTS",
      title: doc.document_name,
      searchableText,
      snippetSource: doc.notes || doc.document_name,
      expandKey: String(doc.id),
    };
  });
}

function buildStateFacilityItems(
  stateRequirements: StateRequirementsResponse,
): ComplianceSearchItem[] {
  return (stateRequirements.facility_requirements ?? []).map((item, idx) => {
    const searchableText = joinFields(
      item.license_or_regulatory_program,
      item.facility_type_name,
      item.legal_code_reference,
      item.staffing_credentialing_requirements,
      item.fire_life_safety_building_oversight,
    );
    return {
      id: `state-facility-${idx}`,
      sourceType: "state_facility",
      tab: "state",
      groupLabel: "STATE REQUIREMENTS",
      title: item.license_or_regulatory_program,
      searchableText,
      snippetSource:
        item.staffing_credentialing_requirements ||
        item.license_or_regulatory_program,
      expandKey: `state-${idx}`,
    };
  });
}

function buildAccreditationItems(
  accreditationData: AccreditationsResponse,
): ComplianceSearchItem[] {
  const items: ComplianceSearchItem[] = [];
  Object.entries(accreditationData).forEach(([programKey, programData]) => {
    if (!programData) return;
    const displayName =
      programData.program ??
      programKey.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase()).trim();

    (programData.sections ?? []).forEach((section, sectionIdx) => {
      const title = section.title || displayName;
      const searchableText = joinFields(displayName, section.title, section.content);
      items.push({
        id: `accreditation-${programKey}-${sectionIdx}`,
        sourceType: "accreditation",
        tab: "other",
        groupLabel: "ACCREDITATIONS",
        title,
        searchableText,
        snippetSource: section.content || section.title || displayName,
        expandKey: programKey,
      });
    });

    if (!(programData.sections ?? []).length) {
      items.push({
        id: `accreditation-${programKey}`,
        sourceType: "accreditation",
        tab: "other",
        groupLabel: "ACCREDITATIONS",
        title: displayName,
        searchableText: displayName,
        snippetSource: displayName,
        expandKey: programKey,
      });
    }
  });
  return items;
}

export function buildComplianceSearchIndex(
  input: BuildComplianceSearchIndexInput,
): ComplianceSearchItem[] {
  const { comprehensiveRequirements, stateRequirements, accreditationData, isStateLevelRequirement } =
    input;
  const items: ComplianceSearchItem[] = [];

  const regulatoryReqs = comprehensiveRequirements?.regulatory_requirements ?? [];
  const federalReqs = regulatoryReqs.filter((req) => !isStateLevelRequirement(req));
  const stateRegReqs = regulatoryReqs.filter((req) => isStateLevelRequirement(req));

  items.push(...buildRegulatoryItems(federalReqs, "federal", isStateLevelRequirement));
  items.push(...buildRegulatoryItems(stateRegReqs, "state", isStateLevelRequirement));
  items.push(...buildCfrItems(comprehensiveRequirements?.cfr_regulation_sections ?? []));
  items.push(...buildOperationalItems(comprehensiveRequirements?.operational_documents ?? []));

  if (stateRequirements?.facility_requirements?.length) {
    items.push(...buildStateFacilityItems(stateRequirements));
  }

  if (accreditationData) {
    items.push(...buildAccreditationItems(accreditationData));
  }

  return items;
}

export function tokenizeSearchQuery(query: string): string[] {
  return query
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
}

export function haystackIncludesAllTokens(haystack: string, query: string): boolean {
  const tokens = tokenizeSearchQuery(query);
  if (tokens.length === 0) return true;
  const lower = haystack.toLowerCase();
  return tokens.every((token) => lower.includes(token));
}

export function filterComplianceSearch(
  items: ComplianceSearchItem[],
  query: string,
): ComplianceSearchItem[] {
  const trimmed = query.trim();
  if (!trimmed) return [];
  return items.filter((item) => haystackIncludesAllTokens(item.searchableText, trimmed));
}

export function extractSearchSnippet(
  text: string,
  query: string,
  maxLen = 140,
): string {
  const source = (text ?? "").trim();
  if (!source) return "";
  const tokens = tokenizeSearchQuery(query);
  if (tokens.length === 0) {
    return source.length <= maxLen ? source : `${source.slice(0, maxLen - 1)}…`;
  }

  const lower = source.toLowerCase();
  let matchIndex = -1;
  for (const token of tokens) {
    const idx = lower.indexOf(token);
    if (idx !== -1 && (matchIndex === -1 || idx < matchIndex)) {
      matchIndex = idx;
    }
  }
  if (matchIndex === -1) {
    return source.length <= maxLen ? source : `${source.slice(0, maxLen - 1)}…`;
  }

  const half = Math.floor((maxLen - 3) / 2);
  const start = Math.max(0, matchIndex - half);
  const end = Math.min(source.length, start + maxLen);
  let snippet = source.slice(start, end);
  if (start > 0) snippet = `…${snippet}`;
  if (end < source.length) snippet = `${snippet}…`;
  return snippet;
}

export interface HighlightSegment {
  text: string;
  highlight: boolean;
}

export function highlightSearchTerms(text: string, query: string): HighlightSegment[] {
  const tokens = tokenizeSearchQuery(query);
  if (!text || tokens.length === 0) {
    return [{ text, highlight: false }];
  }

  const pattern = new RegExp(
    `(${tokens.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`,
    "gi",
  );
  const parts = text.split(pattern);
  return parts
    .filter((part) => part.length > 0)
    .map((part) => ({
      text: part,
      highlight: tokens.some((token) => part.toLowerCase() === token),
    }));
}

export function groupSearchResults(
  results: ComplianceSearchItem[],
): Array<{ groupLabel: string; items: ComplianceSearchItem[] }> {
  const order: string[] = [];
  const map = new Map<string, ComplianceSearchItem[]>();
  for (const item of results) {
    if (!map.has(item.groupLabel)) {
      map.set(item.groupLabel, []);
      order.push(item.groupLabel);
    }
    map.get(item.groupLabel)!.push(item);
  }
  return order.map((groupLabel) => ({
    groupLabel,
    items: map.get(groupLabel)!,
  }));
}

export function regulationTextHitToSearchItem(
  hit: RegulationTextSearchHit,
  index: number,
): ComplianceSearchItem {
  const sectionId = hit.section_identifier ?? "";
  const expandKey =
    hit.regulation_section_id != null
      ? `cfr-${hit.regulation_section_id}`
      : sectionId
        ? `cfr-text-${sectionId}`
        : `text-hit-${index}`;

  return {
    id: `text-${hit.source_type}-${sectionId || index}`,
    sourceType: hit.source_type,
    tab: "federal",
    groupLabel: hit.group_label,
    title: hit.title,
    searchableText: joinFields(hit.title, hit.snippet),
    snippetSource: hit.snippet,
    apiSnippet: hit.snippet,
    expandKey,
    sectionIdentifier: sectionId || undefined,
    sectionUrl: hit.section_url ?? undefined,
  };
}

/** Merge Phase 1 metadata hits with Phase 1.5 text hits; prefer text hit when same CFR section. */
export function mergeSearchResults(
  metadataHits: ComplianceSearchItem[],
  textHits: ComplianceSearchItem[],
): ComplianceSearchItem[] {
  const textSectionKeys = new Set(
    textHits
      .map((h) => (h.sectionIdentifier ?? "").toLowerCase())
      .filter(Boolean),
  );
  const textExpandKeys = new Set(textHits.map((h) => h.expandKey));

  const filteredMetadata = metadataHits.filter((item) => {
    if (item.sourceType !== "cfr") return true;
    const sid = (item.sectionIdentifier ?? "").toLowerCase();
    if (sid && textSectionKeys.has(sid)) return false;
    if (textExpandKeys.has(item.expandKey)) return false;
    return true;
  });

  return [...filteredMetadata, ...textHits];
}

export function isStateLevelRequirement(req: {
  jurisdiction_level?: string;
  jurisdiction_name?: string | null;
  regulatory_body?: string | null;
}): boolean {
  const level = (req.jurisdiction_level ?? "").trim().toLowerCase();
  if (level === "federal" || level === "national" || level === "national/provincial") {
    return false;
  }
  if (
    level === "state" ||
    level === "provincial" ||
    level === "territory" ||
    level === "territorial" ||
    level === "municipal" ||
    level === "local"
  ) {
    return true;
  }
  const body = (req.regulatory_body ?? "").toLowerCase();
  if (/state\s+(department|doh|board|health|agency|licens)/.test(body)) return true;
  if (
    /provincial|territorial/.test(body) &&
    /ministry|department of health|college of|board of/.test(body)
  ) {
    return true;
  }
  if (level && level !== "federal") return true;
  const name = (req.jurisdiction_name ?? "").trim();
  if (name) {
    const lower = name.toLowerCase();
    if (lower === "canada" || lower === "united states" || lower === "usa") return false;
    return true;
  }
  return false;
}
